import { FilterBar } from "@bcc/components/finance/filter-bar";
import { InvoiceDetailDrawer } from "@bcc/components/finance/invoice-detail-drawer";
import { InvoiceTable } from "@bcc/components/finance/invoice-table";
import { TaxReportPanel } from "@bcc/components/finance/tax-report-panel";
import { exportInvoicesWithLines } from "@bcc/components/finance/utils/invoice-export";
import { shouldShowKaffeesteuer } from "@bcc/components/finance/utils/kaffeesteuer";
import { Button } from "@bcc/components/ui/button";
import { useToast } from "@bcc/hooks/use-toast";
import type { InvoiceFilters } from "@bcc/lib/types/invoice";
import { usePayoutState } from "@client-shared/providers/payout";
import useVendorContext from "@client-shared/providers/vendor/useCurrentVendor";
import type { PurchaseOrderInvoice, PurchaseOrderInvoiceLineDetails } from "@shared/types/payout";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { FileSpreadsheet } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setFinanceInvoicesViewExportCsvButtonTestId,
  setFinanceInvoicesViewInvoiceCountTestId,
  setFinanceInvoicesViewTestId,
  setFinanceInvoicesViewTitleTestId,
} from "./finance-invoices-view.utils";

interface FinanceInvoicesViewProps {
  initialFilters: InvoiceFilters;
  testId?: string;
}

interface InvoicesHeaderProps {
  invoiceCount: number;
  canExport: boolean;
  onExportCSV: () => void;
}

function InvoicesHeader({ invoiceCount, canExport, onExportCSV }: Readonly<InvoicesHeaderProps>) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 data-testid={setFinanceInvoicesViewTitleTestId()} className="text-2xl font-semibold tracking-tight text-balance">
          {t("views.financeInvoices.title")}
        </h1>
        <p data-testid={setFinanceInvoicesViewInvoiceCountTestId()} className="mt-1 text-sm text-muted-foreground">
          {t("views.financeInvoices.invoicesFound", { count: invoiceCount })}
        </p>
      </div>
      {canExport && (
        <div className="flex gap-2">
          <Button data-testid={setFinanceInvoicesViewExportCsvButtonTestId()} onClick={onExportCSV} variant="outline" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            {t("views.financeInvoices.exportCsv")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function FinanceInvoicesView({ initialFilters, testId }: Readonly<FinanceInvoicesViewProps>) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<InvoiceFilters>(initialFilters);
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseOrderInvoice | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast } = useToast();
  const [{ currentVendor, initialized: isVendorInitialized }] = useVendorContext();
  const showShipToCountry = (currentVendor?.properties?.shipTo?.length ?? 0) > 1;
  const showKaffeesteuer = shouldShowKaffeesteuer(currentVendor);
  const [payoutState, , { initialize, fetchInvoicesWithDateRange, fetchPurchaseOrderLineDetails }] = usePayoutState();

  // TanStack Table filter state
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const allInvoices = payoutState.invoices || [];
  const isLoading = payoutState.isLoading || false;

  // Initialize PayoutProvider when vendor is ready
  useEffect(() => {
    if (!isVendorInitialized) return;
    initialize();
  }, [isVendorInitialized, initialize]);

  // Sync FilterBar status filters to TanStack Table columnFilters
  useEffect(() => {
    if (filters.status && filters.status.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync TanStack Table columnFilters from FilterBar status selection
      setColumnFilters([
        {
          id: "invoiceStatus",
          value: filters.status,
        },
      ]);
    } else {
      setColumnFilters([]);
    }
  }, [filters.status]);

  // Sync filters.query to TanStack Table globalFilter
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync TanStack Table globalFilter from FilterBar search query
    setGlobalFilter(filters.query || "");
  }, [filters.query]);

  const handleFiltersChange = (newFilters: InvoiceFilters) => {
    setFilters(newFilters);
    // Fetch invoices with new date range when dates change
    if (newFilters.dateFrom !== filters.dateFrom || newFilters.dateTo !== filters.dateTo || newFilters.dateType !== filters.dateType) {
      void fetchInvoicesWithDateRange(newFilters.dateFrom, newFilters.dateTo, newFilters.dateType);
    }
  };

  const handleSearch = (query: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      query: query || undefined,
    }));
  };

  const handleExportCSV = () => {
    try {
      exportInvoicesWithLines({ invoices: allInvoices, showShipToCountry, showKaffeesteuer, t });
      toast({
        title: t("views.financeInvoices.toasts.exportStarted"),
        description: t("views.financeInvoices.toasts.exportingInvoices", {
          count: allInvoices.length,
        }),
      });
    } catch {
      toast({
        title: t("views.financeInvoices.toasts.exportFailed"),
        variant: "destructive",
      });
    }
  };

  const handleInvoiceClick = (invoice: PurchaseOrderInvoice) => {
    setSelectedInvoice(invoice);
    setDrawerOpen(true);
  };

  const handleFetchPODetails = (purchaseOrderId: string): Promise<PurchaseOrderInvoiceLineDetails[]> => {
    if (!selectedInvoice) {
      console.warn("[bcc] Cannot fetch PO details: no invoice selected");
      return Promise.resolve([]);
    }

    return fetchPurchaseOrderLineDetails(purchaseOrderId);
  };

  return (
    <div data-testid={testId ?? setFinanceInvoicesViewTestId("container")} className="space-y-6">
      <InvoicesHeader invoiceCount={allInvoices.length} canExport={allInvoices.length > 0} onExportCSV={handleExportCSV} />

      {/* Filters */}
      <FilterBar filters={filters} onFiltersChange={handleFiltersChange} onSearch={handleSearch} />

      {/* Tax Report Panel */}
      <TaxReportPanel invoices={allInvoices} showKaffeesteuer={showKaffeesteuer} />

      {/* Table */}
      <InvoiceTable
        invoices={allInvoices}
        onInvoiceClick={handleInvoiceClick}
        isLoading={isLoading}
        columnFilters={columnFilters}
        globalFilter={globalFilter}
      />

      {/* Detail Drawer */}
      <InvoiceDetailDrawer
        invoice={selectedInvoice}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onFetchPODetails={handleFetchPODetails}
        showShipToCountry={showShipToCountry}
        showKaffeesteuer={showKaffeesteuer}
        showAuditTab={false}
      />
    </div>
  );
}
